import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type {
  PcmpPendingReportParams,
  PcmpPendingReportSearch,
  PcmpPendingStage,
} from '../types/pcmpReports.types';
import { usePcmpReportFilters } from './usePcmpReportFilters';

const route = getRouteApi('/_auth/pcmp/reports/pending/');

// Reset clears the stage with the shared filters.
const CLEARED_STAGE: Partial<PcmpPendingReportSearch> = { stage: undefined };

/** The Pending report's filters, plus its stage, which applies at once. */
export function usePcmpPendingReportFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpPendingReportSearch) => PcmpPendingReportSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const stage = search.stage;
  const report = usePcmpReportFilters(search, setSearch, {
    activeCount: stage ? 1 : 0,
    cleared: CLEARED_STAGE,
  });
  const { appliedFilters } = report;

  const params = useMemo<PcmpPendingReportParams>(
    () => ({ ...appliedFilters, stage }),
    [appliedFilters, stage],
  );

  const setStage = useCallback(
    (next: PcmpPendingStage | undefined) => {
      setSearch((prev) => ({ ...prev, stage: next }));
    },
    [setSearch],
  );

  return { ...report, stage, params, setStage };
}
